import { useCallback, useEffect, useState } from 'react';
import { api, ApiError, formatRelative } from '../lib/api';
import { Alert, Button, Card, Confirm, Empty, Field, Input, Modal, Spinner } from '../components/ui';

/**
 * Call: remote commentary sessions.
 *
 * A session is one event. It has one host code (the studio signs in with it)
 * and a seat per commentator, each with a code of its own. Codes are sent to
 * people as a link; a person opens it, types their name and is in. The
 * studio and the commentators run on the intercom service
 * (intercom/server/src/call.ts); this page only makes and watches sessions.
 */

interface Seat { id: string; sessionId: string; name: string; code: string; createdAt: string; lastSeenAt: string | null; lastIp: string | null; lastAgent: string | null }
interface Live {
  peerId: string; role: 'seat' | 'host'; seatId: string | null; name: string; ip: string; since: number; sending: string[];
  lazy: boolean; muted: boolean; camOff: boolean;
  health: { quality: string; rttMs: number; lossPct: number; jitterMs: number } | null;
  diag: Record<string, unknown> | null;
}
interface Session { id: string; name: string; hostCode: string; notes: string; createdAt: string; endedAt: string | null; seats: Seat[]; live: Live[]; pictureChannels?: string[]; pictureChannel?: string | null }
/** An intercom channel whose programme picture a session may show its commentators. */
interface Channel { id: string; name: string; colour: string }
interface State { sessions: Session[]; publicUrl: string; channels?: Channel[] }

const QUALITY: Record<string, string> = { good: 'var(--color-good)', fair: 'var(--color-warning)', poor: 'var(--color-critical)' };

export function Call() {
  const [state, setState] = useState<State | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [showEnded, setShowEnded] = useState(false);
  const [confirm, setConfirm] = useState<{ title: string; message: string; label: string; run: () => Promise<unknown> } | null>(null);
  const [diag, setDiag] = useState<{ session: Session; seat: Seat | null } | null>(null);

  const load = useCallback(async (): Promise<void> => {
    try {
      setState(await api.get<State>('/intercom/call/sessions'));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not reach the call service.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const timer = setInterval(() => void load(), 3000);
    return () => clearInterval(timer);
  }, [load]);

  async function act(call: () => Promise<unknown>): Promise<void> {
    try {
      await call();
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.');
    }
  }

  if (loading) return <Spinner label="Loading sessions…" />;
  if (!state) return <Alert tone="critical" title="The call service is not answering">{error}</Alert>;

  const open = state.sessions.filter((s) => !s.endedAt);
  const ended = state.sessions.filter((s) => s.endedAt);
  const link = (code: string) => `${state.publicUrl}/?code=${encodeURIComponent(code)}`;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">Call</h1>
          <p className="text-sm text-ink-muted">
            Remote commentary. Make a session, send each commentator their link, and open the studio link on the computer
            that has the programme sound. They sign in at{' '}
            <a className="underline" href={state.publicUrl} target="_blank" rel="noreferrer">{state.publicUrl.replace(/^https?:\/\//, '')}</a>.
          </p>
        </div>
        <Button variant="primary" onClick={() => setCreating(true)}>New session</Button>
      </div>

      {error && <Alert tone="warning" title="Something did not work">{error}</Alert>}

      {open.length === 0 && <Card><Empty title="No sessions">Make one for the next show.</Empty></Card>}
      {open.map((s) => (
        <SessionCard
          key={s.id} session={s} link={link} act={act} channels={state?.channels ?? []}
          onConfirm={setConfirm} onDiag={(seat) => setDiag({ session: s, seat })}
        />
      ))}

      {ended.length > 0 && (
        <div>
          <button className="text-sm text-ink-muted underline" onClick={() => setShowEnded(!showEnded)}>
            {showEnded ? 'Hide' : 'Show'} {ended.length} ended {ended.length === 1 ? 'session' : 'sessions'}
          </button>
          {showEnded && ended.map((s) => (
            <div key={s.id} className="mt-2 flex flex-wrap items-center justify-between gap-2 rounded-lg bg-surface px-4 py-2 text-sm ring-1 ring-hairline">
              <div>
                <span className="font-medium">{s.name}</span>
                <span className="text-ink-muted"> · {s.seats.length} {s.seats.length === 1 ? 'seat' : 'seats'} · ended {formatRelative(s.endedAt!)}</span>
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" onClick={() => setDiag({ session: s, seat: null })}>Diagnostics</Button>
                <Button variant="ghost" onClick={() => void act(() => api.post(`/intercom/call/sessions/${s.id}/reopen`))}>Reopen</Button>
                <Button
                  variant="ghost"
                  onClick={() => setConfirm({ title: `Delete ${s.name}?`, message: 'Its codes stop working and its diagnostics are gone.', label: 'Delete', run: () => api.delete(`/intercom/call/sessions/${s.id}`) })}
                >
                  Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {creating && <NewSession onClose={() => setCreating(false)} onDone={() => { setCreating(false); void load(); }} />}
      {diag && <DiagnosticsModal session={diag.session} seat={diag.seat} onClose={() => setDiag(null)} />}
      <Confirm
        open={!!confirm}
        title={confirm?.title ?? ''}
        message={confirm?.message}
        confirmLabel={confirm?.label}
        danger
        onConfirm={() => { const c = confirm; setConfirm(null); if (c) void act(c.run); }}
        onClose={() => setConfirm(null)}
      />
    </div>
  );
}

function SessionCard({ session: s, link, act, channels, onConfirm, onDiag }: {
  session: Session; link: (code: string) => string; act: (fn: () => Promise<unknown>) => Promise<void>; channels: Channel[];
  onConfirm: (c: { title: string; message: string; label: string; run: () => Promise<unknown> }) => void;
  onDiag: (seat: Seat | null) => void;
}) {
  const [addSeat, setAddSeat] = useState('');
  const [adding, setAdding] = useState(false);
  const host = s.live.find((p) => p.role === 'host');
  const liveOf = (seatId: string) => s.live.find((p) => p.seatId === seatId);

  return (
    <Card title={s.name} id={s.id}>
      <div className="space-y-3 p-4">
        <Row
          name="Studio" sub={host ? `connected · sending ${host.sending.join(', ') || 'nothing yet'}` : 'not connected'}
          code={s.hostCode} url={link(s.hostCode)} live={host}
          actions={<Button variant="ghost" onClick={() => void act(() => api.post(`/intercom/call/sessions/${s.id}/recode`))} title="Make a new code; the old one stops working">New code</Button>}
        />
        <PictureChannels session={s} channels={channels} act={act} />
        <div className="border-t border-grid" />
        {s.seats.map((seat) => {
          const on = liveOf(seat.id);
          return (
            <Row
              key={seat.id} name={seat.name} code={seat.code} url={link(seat.code)} live={on}
              sub={on
                ? `${on.name !== seat.name ? `signed in as ${on.name} · ` : ''}${on.muted ? 'muted' : on.lazy ? 'lazy: to the studio' : 'on air'}${on.camOff ? '' : ' · camera'}`
                : seat.lastSeenAt ? `last on ${formatRelative(seat.lastSeenAt)}` : 'has not signed in yet'}
              actions={<>
                <Button variant="ghost" onClick={() => onDiag(seat)}>Diagnostics</Button>
                <Button variant="ghost" onClick={() => { const name = prompt('Name for this seat', seat.name); if (name?.trim()) void act(() => api.patch(`/intercom/call/seats/${seat.id}`, { name: name.trim() })); }}>Rename</Button>
                <Button variant="ghost" onClick={() => void act(() => api.post(`/intercom/call/seats/${seat.id}/recode`))} title="Make a new code; the old one stops working">New code</Button>
                <Button variant="ghost" onClick={() => onConfirm({ title: `Remove ${seat.name}?`, message: 'Their code stops working at once.', label: 'Remove', run: () => api.delete(`/intercom/call/seats/${seat.id}`) })}>Remove</Button>
              </>}
            />
          );
        })}
        <form
          className="flex flex-wrap items-center gap-2 pt-1"
          onSubmit={(e) => { e.preventDefault(); if (!addSeat.trim()) return; setAdding(true); void act(() => api.post(`/intercom/call/sessions/${s.id}/seats`, { name: addSeat.trim() })).finally(() => { setAdding(false); setAddSeat(''); }); }}
        >
          <Input value={addSeat} onChange={(e) => setAddSeat(e.target.value)} placeholder="Another commentator's name" className="max-w-xs" />
          <Button disabled={!addSeat.trim()} loading={adding}>Add seat</Button>
          <div className="ml-auto flex gap-1">
            <Button variant="ghost" onClick={() => onDiag(null)}>All diagnostics</Button>
            <Button variant="ghost" onClick={() => onConfirm({ title: `End ${s.name}?`, message: 'Everyone on it is disconnected and the codes stop working. It can be reopened.', label: 'End session', run: () => api.post(`/intercom/call/sessions/${s.id}/end`) })}>End session</Button>
          </div>
        </form>
        {s.notes && <p className="text-sm text-ink-muted">{s.notes}</p>}
      </div>
    </Card>
  );
}

/**
 * Which intercom channels' programme pictures this session's studio may show
 * its commentators (it switches between them on its own page). The first
 * ticked is on at the start; none ticked: the studio's own video.
 */
function PictureChannels({ session: s, channels, act }: { session: Session; channels: Channel[]; act: (fn: () => Promise<unknown>) => Promise<void> }) {
  const chosen = s.pictureChannels ?? [];
  if (!channels.length) return null;
  const toggle = (id: string) => {
    const next = chosen.includes(id) ? chosen.filter((x) => x !== id) : [...chosen, id];
    void act(() => api.patch(`/intercom/call/sessions/${s.id}`, { pictureChannels: next }));
  };
  const current = channels.find((c) => c.id === s.pictureChannel);
  return (
    <div className="text-sm">
      <div className="mb-1 flex flex-wrap items-baseline gap-x-2">
        <span className="font-medium">Picture for commentators</span>
        <span className="text-xs text-ink-muted">
          {chosen.length ? `the studio switches between these · now ${current ? current.name : "the studio's own video"}` : "the studio's own video · tick intercom channels to let the studio show theirs"}
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {channels.map((c) => {
          const on = chosen.includes(c.id);
          return (
            <button
              key={c.id} type="button" onClick={() => toggle(c.id)}
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs ring-1 ${on ? 'bg-raised text-ink ring-ink-secondary' : 'text-ink-muted ring-hairline hover:text-ink'}`}
              aria-pressed={on}
            >
              <span className="h-2 w-2 rounded-full" style={{ background: c.colour }} />
              {on ? '✓ ' : ''}{c.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Row({ name, sub, code, url, live, actions }: { name: string; sub: string; code: string; url: string; live: Live | undefined; actions: React.ReactNode }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* not allowed here */ }
  }
  return (
    <div className="flex flex-wrap items-center gap-3">
      <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: live ? (QUALITY[live.health?.quality ?? ''] ?? 'var(--color-good)') : 'var(--color-ink-muted)' }} />
      <div className="min-w-[160px] flex-1">
        <div className="font-medium">{name}</div>
        <div className="text-[12px] text-ink-muted">
          {sub}
          {live?.health && ` · ${live.health.rttMs} ms · ${live.health.lossPct}% lost`}
          {live && ` · from ${live.ip}`}
        </div>
      </div>
      <button className="rounded bg-raised px-2 py-1 font-mono text-sm ring-1 ring-hairline hover:bg-raised/70" onClick={copy} title="Copy the link">
        {copied ? 'Link copied' : code}
      </button>
      <div className="flex flex-wrap gap-1">{actions}</div>
    </div>
  );
}

function NewSession({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState('');
  const [seats, setSeats] = useState('Commentator 1');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function save() {
    setBusy(true); setError(null);
    try {
      await api.post('/intercom/call/sessions', { name, seats: seats.split(/\n|,/).map((x) => x.trim()).filter(Boolean), notes });
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not make the session.');
    } finally { setBusy(false); }
  }
  return (
    <Modal
      open onClose={onClose} title="New session"
      footer={<><Button onClick={onClose} disabled={busy}>Cancel</Button><Button variant="primary" loading={busy} disabled={!name.trim()} onClick={() => void save()}>Make it</Button></>}
    >
      <div className="space-y-3">
        {error && <Alert tone="critical" title="Not made">{error}</Alert>}
        <Field label="Name" hint="The event. Shown to everyone who signs in.">
          <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder="Saturday · Hawke's Bay v Wellington" />
        </Field>
        <Field label="Commentators" hint="One per line. Each gets a code of their own; more can be added later.">
          <textarea className="w-full rounded-md bg-raised px-3 py-2 text-sm ring-1 ring-hairline" rows={3} value={seats} onChange={(e) => setSeats(e.target.value)} />
        </Field>
        <Field label="Notes" hint="For you: who is coming, which mixer inputs.">
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={2000} />
        </Field>
      </div>
    </Modal>
  );
}

function DiagnosticsModal({ session, seat, onClose }: { session: Session; seat: Seat | null; onClose: () => void }) {
  const [rows, setRows] = useState<Record<string, unknown>[] | null>(null);
  useEffect(() => {
    void api.get<{ diagnostics: Record<string, unknown>[] }>(`/intercom/call/sessions/${session.id}/diagnostics${seat ? `?seat=${seat.id}` : ''}`)
      .then((r) => setRows(r.diagnostics)).catch(() => setRows([]));
  }, [session.id, seat]);
  const seatName = (id: unknown) => session.seats.find((x) => x.id === id)?.name ?? (id ? 'removed seat' : 'Studio');
  const ip = seat?.lastIp;
  return (
    <Modal open onClose={onClose} title={`Diagnostics · ${seat ? seat.name : session.name}`} width="max-w-3xl">
      {seat && (
        <p className="mb-3 text-sm text-ink-muted">
          {ip ? `Last from ${ip}` : 'Has not signed in'}{seat.lastAgent ? ` · ${seat.lastAgent}` : ''}
        </p>
      )}
      {rows === null ? <Spinner label="Loading…" /> : rows.length === 0 ? <Empty title="Nothing reported yet">Reports arrive every half minute while someone is connected.</Empty> : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-grid text-left text-ink-muted">
                <th className="px-2 py-1">When</th>{!seat && <th className="px-2 py-1">Who</th>}
                <th className="px-2 py-1">Line</th><th className="px-2 py-1">RTT</th><th className="px-2 py-1">Lost</th><th className="px-2 py-1">Jitter</th><th className="px-2 py-1">Buffer</th>
                <th className="px-2 py-1">Send</th><th className="px-2 py-1">Receive</th><th className="px-2 py-1">Video</th><th className="px-2 py-1" title="The end's own line test (16 MB down, 4 MB up), run once after it joins">Line test</th><th className="px-2 py-1">Devices</th><th className="px-2 py-1">Browser</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const lt = r.lineTest as { downMbps?: number; upMbps?: number; rttMs?: number } | null | undefined;
                const d = r.devices as { mic?: string; cam?: string; out?: string } | undefined;
                return (
                  <tr key={i} className="border-b border-grid last:border-0">
                    <td className="whitespace-nowrap px-2 py-1">{formatRelative(String(r.at))}</td>
                    {!seat && <td className="px-2 py-1">{seatName(r.seatId)}</td>}
                    <td className="px-2 py-1" style={{ color: QUALITY[String(r.quality)] }}>{String(r.quality ?? '')} {r.transport ? `(${String(r.transport)})` : ''}</td>
                    <td className="px-2 py-1">{String(r.rttMs ?? '')} ms</td>
                    <td className="whitespace-nowrap px-2 py-1" title="sound / picture">{String(r.lossPct ?? '')}%{r.videoLossPct != null ? ` / ${String(r.videoLossPct)}%` : ''}</td>
                    <td className="px-2 py-1">{String(r.jitterMs ?? '')} ms</td>
                    <td className="px-2 py-1">{String(r.bufferMs ?? '')} ms</td>
                    <td className="px-2 py-1">{String(r.sendKbps ?? '')} k</td>
                    <td className="px-2 py-1">{String(r.recvKbps ?? '')} k</td>
                    <td className="whitespace-nowrap px-2 py-1">{r.videoRes ? `${String(r.videoRes)} ${String(r.videoFps)}fps` : '–'}</td>
                    <td className="whitespace-nowrap px-2 py-1">{lt ? `↓${lt.downMbps} ↑${lt.upMbps} Mb/s` : '–'}</td>
                    <td className="max-w-[200px] truncate px-2 py-1" title={d ? `${d.mic} / ${d.cam} / ${d.out}` : ''}>{d ? `${d.mic ?? ''} · ${d.cam ?? ''}` : '–'}</td>
                    <td className="max-w-[220px] truncate px-2 py-1" title={String(r.browser ?? '')}>{String(r.platform ?? '')} {String(r.browser ?? '').replace(/^Mozilla\/5\.0 \([^)]*\) /, '').slice(0, 60)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
